import { useEffect, useState } from "react";
import { Lock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useGetDealCompensation, usePutDealCompensation } from "@/hooks/useCompensation";
import { formatMoney } from "@/utils/deals.utils";

const BASIS_LABEL = {
  purchase: "Based on the purchase price",
  asking: "Based on current asking price",
  none: "Add a price to see the amount",
} as const;

/**
 * Agent compensation (client feedback, items 10 and 11): a private %, and the
 * dollar amount it comes to. Draws nothing unless the server says this viewer
 * may see it — the deal's agent, or an owner, brokerage admin or team lead —
 * so a coordinator gets no empty box and a client never reaches this at all.
 * Saves when the field loses focus or on Enter; the amount comes back from the
 * server, worked out from the deal's current price.
 */
export default function AgentCompensationCard({ dealId }: { dealId: string }) {
  const { data } = useGetDealCompensation(dealId);
  const { mutate: save, isPending } = usePutDealCompensation();
  const stored = data?.visible ? data.pct : null;
  const [draft, setDraft] = useState("");

  useEffect(() => { setDraft(stored == null ? "" : String(stored)); }, [stored]);

  if (!data?.visible) return null;

  const commit = () => {
    const value = draft.trim() === "" ? null : Number(draft);
    if (value !== null && (Number.isNaN(value) || value < 0 || value > 100)) {
      setDraft(stored == null ? "" : String(stored));
      return;
    }
    if (value === stored || (value === 0 && stored == null)) return;
    save({ dealId, pct: value || null });
  };

  return (
    <section className="rounded-xl border border-dash-border bg-white p-5" data-testid="agent-compensation">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-dash-ink">Agent compensation</h3>
        <span className="inline-flex items-center gap-1 text-xs text-dash-muted"><Lock className="size-3.5" aria-hidden /> Private</span>
      </div>
      <div className="mt-3 flex items-center gap-3">
        <label htmlFor={`comp-pct-${dealId}`} className="sr-only">Agent compensation percent</label>
        <Input
          id={`comp-pct-${dealId}`}
          type="number"
          inputMode="decimal"
          min={0}
          max={100}
          step={0.1}
          placeholder="2.5"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
          disabled={isPending}
          className="h-10 w-24"
          data-testid="agent-compensation-pct"
        />
        <span className="text-sm text-dash-muted">%</span>
        <span className="ml-auto font-semibold tabular-nums text-dash-ink" data-testid="agent-compensation-amount">
          {formatMoney(data.amount) || "—"}
        </span>
      </div>
      <p className="mt-2 text-xs text-dash-faint">{BASIS_LABEL[data.price_basis]} · Only you and your managers see this.</p>
    </section>
  );
}
